// src/pages/Gallery.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { collection, onSnapshot, query, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import { optimizeUrl } from "../utils/uploadImage";
import "./Gallery.css";

// Purani static images (public/images folder se)
const staticImages = [];

const SWIPE_THRESHOLD = 50; // px

const ZoomIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.35-4.35M11 8v6M8 11h6" />
  </svg>
);

function Gallery() {
  const [uploaded, setUploaded] = useState([]);
  const [openIndex, setOpenIndex] = useState(null);

  const touchStart = useRef({ x: null, y: null });
  const lastFocusedRef = useRef(null);
  const closeBtnRef = useRef(null);

  // Admin Panel se upload ki hui images (Firestore + Cloudinary)
  useEffect(() => {
    const q = query(collection(db, "gallery"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(
      q,
      (snap) => setUploaded(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (error) => console.error("Gallery load error:", error.message)
    );
    return () => unsub();
  }, []);

  // Uploaded (nayi pehle) aur static images ko ek list mein jama karein
  const images = useMemo(
    () => [
      ...uploaded
        .filter((img) => img.imageUrl)
        .map((img) => ({
          key: img.id,
          thumb: optimizeUrl(img.imageUrl, 600),
          full: optimizeUrl(img.imageUrl, 1600),
          alt: img.title || "School gallery",
          caption: img.title || "",
        })),
      ...staticImages.map((src, index) => ({
        key: `static-${index}`,
        thumb: src,
        full: src,
        alt: `Gallery photo ${index + 1}`,
        caption: "",
      })),
    ],
    [uploaded]
  );

  const total = images.length;
  const isOpen = openIndex !== null;
  const current = isOpen ? images[openIndex] : null;

  const openLightbox = useCallback((index) => {
    lastFocusedRef.current = document.activeElement;
    setOpenIndex(index);
  }, []);

  const closeLightbox = useCallback(() => setOpenIndex(null), []);

  const showNext = useCallback(() => {
    if (!total) return;
    setOpenIndex((i) => (i === null ? null : (i + 1) % total));
  }, [total]);

  const showPrev = useCallback(() => {
    if (!total) return;
    setOpenIndex((i) => (i === null ? null : (i - 1 + total) % total));
  }, [total]);

  // Agar list badal jaye aur open index out of range ho jaye, to lightbox band karein
  useEffect(() => {
    if (openIndex !== null && openIndex >= total) {
      setOpenIndex(null);
    }
  }, [total, openIndex]);

  // Scroll lock, keyboard controls and focus management while lightbox is open
  useEffect(() => {
    if (!isOpen) return;

    document.body.classList.add("lightbox-open");
    closeBtnRef.current?.focus();

    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      else if (e.key === "ArrowRight") showNext();
      else if (e.key === "ArrowLeft") showPrev();
    };

    window.addEventListener("keydown", onKey);
    return () => {
      document.body.classList.remove("lightbox-open");
      window.removeEventListener("keydown", onKey);
      // Lightbox band hone par focus wapas thumbnail par
      lastFocusedRef.current?.focus?.();
    };
  }, [isOpen, closeLightbox, showNext, showPrev]);

  // Agli aur pichli image pehle se load karein taake next/prev par blank na dikhe
  useEffect(() => {
    if (!isOpen || total < 2) return;
    [1, -1].forEach((offset) => {
      const idx = (openIndex + offset + total) % total;
      const preload = new Image();
      preload.src = images[idx].full;
    });
  }, [isOpen, openIndex, total, images]);

  // Touch swipe: horizontal swipe = next/prev, vertical scroll ko ignore karein
  const handleTouchStart = (e) => {
    const t = e.touches[0];
    touchStart.current = { x: t.clientX, y: t.clientY };
  };

  const handleTouchEnd = (e) => {
    const { x, y } = touchStart.current;
    touchStart.current = { x: null, y: null };
    if (x === null) return;

    const t = e.changedTouches[0];
    const dx = t.clientX - x;
    const dy = t.clientY - y;

    if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0) showNext();
      else showPrev();
    }
  };

  // Backdrop click band karta hai, lekin image ya buttons par click nahi
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) closeLightbox();
  };

  const lightbox =
    isOpen && current
      ? createPortal(
          <div
            className="lightbox"
            role="dialog"
            aria-modal="true"
            aria-label="Photo viewer"
            onClick={handleBackdropClick}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <div className="lightbox-content">
              <img
                className="lightbox-img"
                src={current.full}
                alt={current.alt}
                draggable="false"
              />
              {current.caption && (
                <p className="lightbox-caption">{current.caption}</p>
              )}
              {total > 1 && (
                <span className="lightbox-counter" aria-live="polite">
                  {openIndex + 1} / {total}
                </span>
              )}
            </div>

            <button
              ref={closeBtnRef}
              type="button"
              className="lightbox-close"
              onClick={closeLightbox}
              aria-label="Close photo viewer"
            >
              &times;
            </button>

            {total > 1 && (
              <>
                <button
                  type="button"
                  className="lightbox-nav lightbox-prev"
                  onClick={showPrev}
                  aria-label="Previous photo"
                >
                  &#8249;
                </button>
                <button
                  type="button"
                  className="lightbox-nav lightbox-next"
                  onClick={showNext}
                  aria-label="Next photo"
                >
                  &#8250;
                </button>
              </>
            )}
          </div>,
          document.body
        )
      : null;

  return (
    <div className="gallery-container">
      <header className="gallery-hero">
        <div className="gallery-hero-inner">
          <span className="gallery-eyebrow">Campus Life</span>
          <h1 className="gallery-title">School Gallery</h1>
          <p className="gallery-subtitle">
            Moments from our classrooms, events and campus.
          </p>
        </div>
      </header>

      <main className="gallery-wrap">
        {total === 0 ? (
          <p className="gallery-empty">No photos have been added yet.</p>
        ) : (
          <div className="gallery-grid">
            {images.map((img, index) => (
              <div
                key={img.key}
                className="gallery-item"
                role="button"
                tabIndex={0}
                aria-label={`Open photo: ${img.alt}`}
                onClick={() => openLightbox(index)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openLightbox(index);
                  }
                }}
              >
                <img
                  src={img.thumb}
                  alt={img.alt}
                  loading="lazy"
                  decoding="async"
                  draggable="false"
                />
                <span className="zoom-badge" aria-hidden="true">
                  <ZoomIcon />
                </span>
              </div>
            ))}
          </div>
        )}
      </main>

      {lightbox}
    </div>
  );
}

export default Gallery;